import { alpha, makeStyles } from '@material-ui/core';
import { ReactElement, useState, useEffect, useRef } from 'react';
import { SvgIcon } from 'components/Shared/SvgIcon';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Virtual, Navigation } from 'swiper';
import Skeleton from '@material-ui/lab/Skeleton';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';

SwiperCore.use([Virtual, Navigation]);
interface IProps {
  children: ReactElement[];
  fetching?: boolean;
  fetchMore?: () => void;
  hasMoreToFetch?: boolean;
}

const useStyles = makeStyles((theme) => ({
  carouselWrapper: {
    position: 'relative',
    '& #showcase-card': {
      width: '100%',
    },
    '&:hover > div': {
      opacity: 1,
    },
  },
  carouselArrows: {
    position: 'absolute',
    top: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    transition: '0.3s ease',
    cursor: 'pointer',
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    opacity: 0,
    backgroundColor: alpha(theme.palette.neutrals.black, 0.8),
    width: '4.5vw',
    '&:first-of-type': {
      transform: 'scale(-1)',
      left: 0,
    },
    '&:last-of-type': {
      right: 0,
    },
    '&[aria-disabled=true]': {
      pointerEvents: 'none',
      display: 'none',
    },
    '& svg': {
      transition: '50ms ease',
      '&:hover': {
        transform: 'scale(1.4)',
      },
    },
  },
  skeleton: {
    background: theme.palette.dark[600],
    borderRadius: 8,
    transform: 'none',
    maxWidth: 'calc(100% - 8px)',
  },
}));

const CarouselDesktop = (props: IProps) => {
  const [isEnd, setIsEnd] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const swiperRef = useRef<SwiperCore>(null);
  const childrenLength = useRef(props.children?.length ?? 0);
  const activeIndex = useRef(0);
  const slidesPerView = useRef(window.innerWidth >= 2560 ? 7 : 5);
  const classes = useStyles();

  const handleNext = () => {
    swiperRef.current?.slideNext();
    handleFetchMore();
    if (swiperRef.current?.isEnd) {
      setIsEnd(!props?.hasMoreToFetch || props?.fetching);
    } else {
      setIsEnd((isEnd) => {
        if (isEnd) return false;
        return isEnd;
      });
    }
  };

  const handlePrev = () => {
    swiperRef.current?.slidePrev();
    setIsEnd((isEnd) => {
      if (isEnd !== swiperRef.current?.isEnd) {
        return swiperRef.current?.isEnd;
      }
      return isEnd;
    });
  };

  const handleFetchMore = () => {
    if (!props.fetchMore) return;
    const realIndex = (props.children ?? []).length;
    const skeletonElement = document.getElementById('swiper-slide-skeleton-' + realIndex);
    if (!skeletonElement) return;
    const { bottom } = skeletonElement.getBoundingClientRect();
    if (bottom <= window.innerHeight) {
      activeIndex.current = swiperRef.current.activeIndex;
      props.fetchMore();
    }
  };

  useEffect(() => {
    if (!swiperRef.current || childrenLength.current === 0 || childrenLength.current === props.children.length) return;
    childrenLength.current = props.children.length;
    swiperRef.current.slideTo(activeIndex.current + slidesPerView.current, 0);
  }, [props.children]);

  return (
    <div className={classes.carouselWrapper}>
      <div
        id='showcase-carousel-arrow-left'
        className={classes.carouselArrows}
        aria-disabled={!hasPrev}
        onClick={handlePrev}
      >
        <SvgIcon name='boldArrowRight' />
      </div>
      <Swiper
        id='showcase-carousel'
        speed={500}
        preventInteractionOnTransition={true}
        allowTouchMove={false}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        slidesPerView={5.4}
        slidesPerGroup={5}
        spaceBetween={16}
        slidesOffsetAfter={32}
        slidesOffsetBefore={32}
        watchOverflow
        breakpoints={{
          2560: {
            slidesPerView: 7.4,
            slidesPerGroup: 7,
          },
        }}
        virtual={{
          cache: true,
        }}
        onActiveIndexChange={(swiper) => {
          if (swiper.activeIndex > 0) {
            setHasPrev(true);
          } else {
            setHasPrev(false);
          }
        }}
      >
        {props.children.map((child, index) => (
          <SwiperSlide key={'swiper-slide-' + index} virtualIndex={index}>
            {child}
          </SwiperSlide>
        ))}
        {props.hasMoreToFetch &&
          Array.from(Array(slidesPerView.current)).map((_, index) => {
            const realIndex = (props.children ?? []).length + index;
            const id = 'swiper-slide-skeleton-' + realIndex;
            return (
              <SwiperSlide id={id} key={id} virtualIndex={realIndex}>
                <Skeleton className={classes.skeleton}>{props.children[0]}</Skeleton>
              </SwiperSlide>
            );
          })}
      </Swiper>
      <div
        id='showcase-carousel-arrow-right'
        className={classes.carouselArrows}
        aria-disabled={isEnd || props.children.length <= slidesPerView.current}
        onClick={handleNext}
      >
        <SvgIcon name='boldArrowRight' />
      </div>
    </div>
  );
};

export default CarouselDesktop;
